import { ProductAccountDTO, QuoteStartPayload, TrialStartPayload, TrialStartResultDTO } from 'api-models'
import { ApiClient } from '../api-client'

async function createProductAccount(payload: any): Promise<ProductAccountDTO> {
	const { data } = await ApiClient.client.post('/onboarding/start', payload)
	return data.data
}

async function setAccountLink(
	accountId: string,
	code: string,
	platform: string
): Promise<ProductAccountDTO> {
	const { data } = await ApiClient.client.patch(
		`/onboarding/${accountId}/link`,
		{ auth_code: code, platform: platform }
	)
	return data.data
}

async function addAccount(
	accountId: string,
	payload: {
		account_number: string
		link_id?: string
		set_tracking_template?: boolean
		set_custom_audience?: boolean
		manager?: boolean
	}
): Promise<{ type: string; data: any }> {
	const { data } = await ApiClient.client.post(
		`/onboarding/${accountId}/ad-account`,
		payload
	)
	return data.data
}

async function addAccounts(
	accountId: string,
	payload: {
		account_numbers: string[]
		link_id?: string
		set_tracking_template?: boolean
		set_custom_audience?: boolean
	}
): Promise<{ type: string; data: any }> {
	const { data } = await ApiClient.client.post(
		`/onboarding/${accountId}/ad-accounts`,
		payload
	)
	return data.data
}

async function startTrial(
	accountId: string,
	payload: TrialStartPayload & { coupon_code?: string | null }
): Promise<TrialStartResultDTO> {
	const { data } = await ApiClient.client.post(
		`/onboarding/${accountId}/trial/start`,
		payload
	)
	return data.data
}

async function startQuote(
	accountId: string,
	payload: QuoteStartPayload
): Promise<TrialStartResultDTO> {
	const { data } = await ApiClient.client.post(
		`/onboarding/${accountId}/quote/start`,
		payload
	)
	return data.data
}

async function setupExpertMeetingClicked(
	accountId: string
): Promise<ProductAccountDTO> {
	const { data } = await ApiClient.client.post(
		`/onboarding/${accountId}/setup-meeting-clicked`,
		{}
	)
	return data.data
}

async function optOutTracking(
	accountId: string,
	assetId: string
): Promise<ProductAccountDTO> {
	const { data } = await ApiClient.client.post(
		`/onboarding/${accountId}/opt-out-tracking`,
		{ asset_id: assetId }
	)
	return data.data
}

async function updateSetup(
	accountId: string,
	payload: {
		step?: string
		auth_id?: string | null
		account_number?: string | null
		domain?: string
		configuration?: any
		selected_plan_details?: {
			tier?: string | null
			price_id?: string | null
			price?: number | null
			is_custom?: boolean
			billing_period?: 'monthly' | 'yearly' | 'quarterly' | null
		} | null
		selected_plan_at?: Date | null
		tracking_code_opted_out?: boolean
		onboardingPath?:
			| 'self-onboarding'
			| 'guided-onboarding'
			| 'self-onboarding-converted'
			| 'abandoned'
		scheduled_demo?: boolean
	}
): Promise<ProductAccountDTO> {
	const { data } = await ApiClient.client.patch(
		`/onboarding/${accountId}/setup`,
		payload
	)
	return data.data
}

async function updateTrackerVerificationStatus(
	accountId: string,
	status: 'success' | 'failed_server_side_tracking' | 'failed_tracking_code'
): Promise<void> {
	await ApiClient.client.patch(
		`/onboarding/${accountId}/tracker-verification-status`,
		{ status }
	)
}

async function createAsset(
	accountId: string,
	payload: {
		domain: string
		protection_enabled?: boolean
		/** The website's sector and industry (config SectorTypes / IndustryTypes). */
		sector?: string | null
		industry?: string | null
		/** Free text when the sector is "other" (no listed sector fits).
		 * PROTOTYPE CONTRACT (lane onboarding-flow-screening). */
		industry_other?: string | null
	}
): Promise<any> {
	const { data } = await ApiClient.client.post(`/setup/${accountId}/asset`, payload)
	return data.data
}

async function setAccountLinkForAsset(
	accountId: string,
	assetId: string,
	code: string,
	platform: string
): Promise<ProductAccountDTO & { link: any }> {
	const { data } = await ApiClient.client.patch(
		`/setup/${accountId}/${assetId}/link`,
		{ auth_code: code, platform: platform }
	)
	return data.data
}

async function getTrackingInfo(
	accountId: string,
	assetId: string,
	linkId: string,
	accountNumber: string,
	platform: string,
	options?: { customer_id?: string; ad_account_id?: string }
): Promise<{
	tracking_template: string | null
	final_url_suffix: string | null
	is_clickguard_tt: boolean
	is_clickguard_suffix: boolean
	campaigns_to_update: Array<{ id: string; name: string }>
}> {
	let url = `/setup/${accountId}/${assetId}/tracking-info?link_id=${linkId}&account_number=${accountNumber}&platform=${platform}`
	if (options?.customer_id) url += `&customer_id=${options.customer_id}`
	if (options?.ad_account_id) url += `&ad_account_id=${options.ad_account_id}`
	const { data } = await ApiClient.client.get(url)
	return data.data
}

async function addAdAccountToAsset(
	accountId: string,
	assetId: string,
	payload: {
		account_number: string
		link_id: string
		set_tracking_template?: boolean
		set_custom_audience?: boolean
		pixel_id?: string | null
		custom_url_suffix?: string
	}
): Promise<{ type: string; data: any }> {
	const { data } = await ApiClient.client.post(
		`/setup/${accountId}/${assetId}/ad-account`,
		payload
	)
	return data.data
}

async function getPixels(
	accountId: string,
	assetId: string,
	linkId: string,
	accountNumber: string
): Promise<Array<{ id: string; name: string }>> {
	const { data } = await ApiClient.client.get(
		`/setup/${accountId}/${assetId}/pixels?link_id=${linkId}&account_number=${accountNumber}`
	)
	return data.data
}

async function checkInterstitialEligibility(
	accountId: string
): Promise<{ showInterstitial: boolean }> {
	const { data } = await ApiClient.client.get(
		`/onboarding/${accountId}/check-interstitial-eligibility`
	)
	return data.data
}

/**
 * Spend screening: where the ad account's last-30-day spend puts it. The
 * backend owns the thresholds and returns only the tier, never an amount.
 * Uses the same server-side policy enforced by the final connection endpoint.
 */
export type AdAccountSpendTier =
	| 'zero'
	| 'low'
	| 'screening'
	| 'vip'
	| 'qualified'

async function checkAdAccountSpend(
	accountId: string,
	assetId: string,
	linkId: string,
	accountNumber: string,
	platform: string,
	identity?: { customer_id?: string; ad_account_id?: string }
): Promise<{ tier: AdAccountSpendTier }> {
	const { data } = await ApiClient.client.post(
		`/setup/${accountId}/${assetId}/spend-check`,
		{ link_id: linkId, account_number: accountNumber, ...identity }
	)
	return data.data
}

export const Onboarding = {
	checkAdAccountSpend,
	createProductAccount,
	updateSetup,
	updateTrackerVerificationStatus,
	addAccount,
	addAccounts,
	startTrial,
	startQuote,
	setAccountLink,
	setupExpertMeetingClicked,
	createAsset,
	setAccountLinkForAsset,
	addAdAccountToAsset,
	optOutTracking,
	getPixels,
	checkInterstitialEligibility,
	getTrackingInfo
}
